import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Star, ShoppingCart, BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { mockBooks } from '../mock';
import { useCart } from '../context/CartContext';

const BookDetail = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const book = mockBooks.find(b => b.id === id);
  const [quantity, setQuantity] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);

  if (!book) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Book not found</h2>
          <Button asChild>
            <Link to="/books">Back to Books</Link>
          </Button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(book, quantity);
  };

  const nextPage = () => {
    if (currentPage < book.sample.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Back Button */}
        <Button asChild variant="ghost" className="mb-8">
          <Link to="/books">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Books
          </Link>
        </Button>

        {/* Book Details */}
        <div className="grid md:grid-cols-2 gap-12 mb-12">
          {/* Cover Image */}
          <div>
            <img
              src={book.coverImage}
              alt={book.title}
              className="w-full rounded-lg shadow-2xl"
            />
          </div>

          {/* Book Info */}
          <div className="space-y-6">
            <div>
              <div className="inline-block bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full mb-4">
                {book.category}
              </div>
              <h1 className="text-4xl font-bold text-gray-800 mb-2">{book.title}</h1>
              {book.subtitle && (
                <p className="text-xl text-gray-600 mb-4">{book.subtitle}</p>
              )}
              <p className="text-lg text-gray-700 mb-4">by {book.author}</p>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star key={`star-${i}`} className={`h-5 w-5 ${i < Math.floor(book.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                ))}
              </div>
              <span className="text-gray-600">{book.rating} ({book.reviews} reviews)</span>
            </div>

            <p className="text-gray-700 leading-relaxed text-lg">{book.description}</p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Age Range</p>
                <p className="font-semibold text-gray-800">{book.ageRange}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Pages</p>
                <p className="font-semibold text-gray-800">{book.pages}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {book.themes.map((theme) => (
                <span key={theme} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                  {theme}
                </span>
              ))}
            </div>

            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-3xl font-bold text-gray-800">${book.price}</span>
                <div className="flex items-center space-x-3">
                  <span className="text-gray-600">Quantity:</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  >
                    -
                  </Button>
                  <span className="w-12 text-center font-semibold">{quantity}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setQuantity(quantity + 1)}
                  >
                    +
                  </Button>
                </div>
              </div>
              <Button
                onClick={handleAddToCart}
                size="lg"
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                Add to Cart
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="preview" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto">
            <TabsTrigger value="preview">Preview Pages</TabsTrigger>
            <TabsTrigger value="details">More Details</TabsTrigger>
          </TabsList>
          
          <TabsContent value="preview" className="mt-8">
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="relative bg-gradient-to-br from-blue-50 to-purple-50">
                  {/* Reader Interface */}
                  <div className="max-w-4xl mx-auto p-8">
                    <div className="bg-white rounded-lg shadow-xl p-8 min-h-[500px]">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm text-gray-600">Page {currentPage + 1} of {book.sample.length}</span>
                        <BookOpen className="h-5 w-5 text-blue-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-800 mb-6">
                        {book.sample[currentPage].content}
                      </h3>
                      <img
                        src={book.sample[currentPage].illustration}
                        alt={`Page ${currentPage + 1}`}
                        className="w-full rounded-lg mb-6"
                      />
                      <p className="text-gray-700 leading-relaxed">
                        This is a sample preview. Full interactive reading experience available after purchase!
                      </p>
                    </div>
                    
                    {/* Navigation */}
                    <div className="flex justify-between items-center mt-6">
                      <Button
                        onClick={prevPage}
                        disabled={currentPage === 0}
                        variant="outline"
                      >
                        <ChevronLeft className="mr-2 h-4 w-4" /> Previous
                      </Button>
                      <span className="text-sm text-gray-600">
                        Sample Preview - {book.sample.length} pages
                      </span>
                      <Button
                        onClick={nextPage}
                        disabled={currentPage === book.sample.length - 1}
                        variant="outline"
                      >
                        Next <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="details" className="mt-8">
            <Card>
              <CardContent className="p-8">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">What's Inside</h3>
                    <p className="text-gray-700">
                      {book.description}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Key Learning Themes</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      {book.themes.map((theme) => (
                        <li key={theme}>{theme}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Perfect For</h3>
                    <p className="text-gray-700">
                      Children aged {book.ageRange} who love adventure and are curious about the world around them.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default BookDetail;